import { DateTime } from 'luxon';
import type { CollectionTicketClaimDTO } from '@/api/trafikportalen';
import { getPeriodStatus } from '@/helpers/periodStatus';
import { dateFormat } from '@/utils/dateFormat';

export const PERIOD_TIME_ZONE = 'Europe/Stockholm';

const UTC_DAY_BOUNDARY = /T(00:00:00|23:59:59)(\.0+)?(Z|\+00:00)$/;

const stockholmNow = (): DateTime => DateTime.now().setZone(PERIOD_TIME_ZONE);

const stockholmMidnight = (date: string): string =>
  DateTime.fromISO(date, { zone: PERIOD_TIME_ZONE })
    .startOf('day')
    .toISO({ suppressMilliseconds: true })!;

export type PeriodDateBoundary = 'start' | 'end';

const isStockholmMidnight = (value: DateTime): boolean =>
  value.hour === 0 &&
  value.minute === 0 &&
  value.second === 0 &&
  value.millisecond === 0;

/**
 * The calendar date of a period start or end. A start is midnight on that
 * date in Europe/Stockholm. An end is midnight at the start of the next day,
 * so the picked day is included. Older periods stored the UTC day boundary and
 * are read back in UTC. Other instants, such as "Avsluta omedelbart", are
 * shown in Europe/Stockholm.
 */
export const toPeriodDate = (
  timestamp: string,
  boundary: PeriodDateBoundary = 'start',
): string => {
  if (UTC_DAY_BOUNDARY.test(timestamp)) {
    return DateTime.fromISO(timestamp, { zone: 'utc' }).toFormat(dateFormat);
  }
  const stockholm = DateTime.fromISO(timestamp, { setZone: true }).setZone(
    PERIOD_TIME_ZONE,
  );
  if (!stockholm.isValid) return timestamp;
  const shown =
    boundary === 'end' && isStockholmMidnight(stockholm)
      ? stockholm.minus({ days: 1 })
      : stockholm;
  return shown.toFormat(dateFormat);
};

const startDate = (claim: CollectionTicketClaimDTO): string | null =>
  claim.valid_from ? toPeriodDate(claim.valid_from) : null;

/** Latest date that is a valid end date for every claim in the set. */
export const earliestAllowedEndDate = (
  claims: CollectionTicketClaimDTO[],
  today: string,
): string => {
  let earliest = today;
  for (const claim of claims) {
    const start = startDate(claim);
    if (!start) continue;
    const candidate =
      getPeriodStatus(claim) === 'upcoming'
        ? DateTime.fromISO(start).plus({ days: 1 }).toFormat('yyyy-MM-dd')
        : start > today
          ? start
          : today;
    if (candidate > earliest) earliest = candidate;
  }
  return earliest;
};

export const periodEndDateError = (
  endDate: string,
  claims: CollectionTicketClaimDTO[],
  today: string,
): boolean => {
  if (!endDate) return false;
  return endDate < earliestAllowedEndDate(claims, today);
};

export const isSameEndDate = (
  endDate: string,
  claims: CollectionTicketClaimDTO[],
  endImmediately: boolean,
  today: string,
): boolean => {
  if (!endDate || claims.length === 0) return true;
  if (endImmediately && endDate === today) return false;
  return claims.every(
    (claim) =>
      !!claim.valid_to && toPeriodDate(claim.valid_to, 'end') === endDate,
  );
};

export const toPeriodEndTimestamp = (
  endDate: string,
  endImmediately: boolean,
): string => {
  if (endImmediately) {
    return stockholmNow()
      .plus({ seconds: 2 })
      .toISO({ suppressMilliseconds: true })!;
  }
  return DateTime.fromISO(endDate, { zone: PERIOD_TIME_ZONE })
    .plus({ days: 1 })
    .startOf('day')
    .toISO({ suppressMilliseconds: true })!;
};

/**
 * True when a period has already started and its calendar dates overlap the
 * current or previous month in Europe/Stockholm. Dates are the same ones shown
 * in the period list, so an end stored as midnight is the previous day.
 */
export const hasPeriodActiveThisOrPreviousMonth = (
  claims: { valid_from?: string; valid_to?: string }[],
  now: DateTime = DateTime.now(),
): boolean => {
  const stockholmNow = now.setZone(PERIOD_TIME_ZONE);
  const today = stockholmNow.toFormat(dateFormat);
  const windowStart = stockholmNow
    .startOf('month')
    .minus({ months: 1 })
    .toFormat(dateFormat);
  const windowEnd = stockholmNow.endOf('month').toFormat(dateFormat);

  return claims.some((claim) => {
    const periodStart = claim.valid_from
      ? DateTime.fromISO(claim.valid_from)
      : undefined;
    if (periodStart && !periodStart.isValid) return false;
    const periodEnd = claim.valid_to
      ? DateTime.fromISO(claim.valid_to)
      : undefined;
    if (periodEnd && !periodEnd.isValid) return false;

    const start = claim.valid_from
      ? toPeriodDate(claim.valid_from, 'start')
      : '0000-01-01';
    if (start > today) return false;

    const end = claim.valid_to
      ? toPeriodDate(claim.valid_to, 'end')
      : '9999-12-31';

    return start <= windowEnd && end >= windowStart;
  });
};

export const toPeriodStartTimestamp = (startDate: string): string => {
  if (startDate === stockholmNow().toFormat('yyyy-MM-dd')) {
    return stockholmNow()
      .plus({ seconds: 2 })
      .toISO({ suppressMilliseconds: true })!;
  }
  return stockholmMidnight(startDate);
};
